import styled from 'styled-components';

import rem from 'polished/lib/helpers/rem';
import toColorString from 'polished/lib/color/toColorString';

import rgbaToHex from '@decisiv/ui-components/lib/utils/rgbaToHex';
import color from '@decisiv/design-tokens/lib/color';
import spacing from '@decisiv/design-tokens/lib/spacing';

// Real styled components rather than styled-components' `css` prop: portal's
// babel config has no babel-plugin-styled-components, so a `css={...}` would be
// dropped without warning. Same constraint the rest of this page works under.

/** A step's body: heading block, then the fields, 20px apart as in the mockup. */
export const StepBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(spacing.base)};
`;

/** The heading row: title and subtitle on the left, "Required Fields" opposite. */
export const StepHeading = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${rem(spacing.base)};
`;

export const RequiredMarker = styled.div`
  display: flex;
  align-items: center;
  gap: ${rem(spacing.base / 4)};
  flex-shrink: 0;
  white-space: nowrap;
`;

/** A group of fields with the mockup's 10px rhythm between them. */
export const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(spacing.base / 2)};
`;

/**
 * The country code and the number, plus whatever is said about the pair.
 *
 * The message belongs to the row rather than to the number field: the mockup puts
 * it at x=0 spanning all 590px (Figma 787:76709, 787:76731), and hanging it off
 * the number's `helpMessage` indented it 135px to sit under that column alone.
 */
export const PhoneField = styled.div`
  display: flex;
  flex-direction: column;

  /* What InputContainer gives its own help and warning messages. */
  > * ~ * {
    margin-top: ${rem(spacing.base / 2)};
  }
`;

/**
 * The country code sits in a fixed 130px column beside the number, as in the
 * mockup, rather than sharing the width evenly.
 *
 * 5px, not the 10px the other pairs use: this is the design's one tighter gap
 * (Frame 32 puts the Select at x=0 w=130 and the field at x=135).
 */
export const PhoneRow = styled.div`
  display: flex;
  gap: ${rem(spacing.base / 4)};
`;

export const PhoneCodeColumn = styled.div`
  width: ${rem(130)};
  flex-shrink: 0;
`;

export const PhoneNumberColumn = styled.div`
  flex: 1;
  min-width: 0;
`;

/** A labelled section on the Permissions step. */
export const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(spacing.base / 2)};
`;

/** A section heading that carries the required marker, as the field labels do. */
export const SectionHeading = styled.div`
  display: flex;
  align-items: center;
  gap: ${rem(spacing.base / 4)};
`;

export const CheckboxList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(spacing.base / 2)};
`;

/** The Member/Manager radios, indented under the Management Role checkbox. */
export const NestedChoices = styled.div`
  margin-left: ${rem(spacing.base * 1.5)};
`;

/** The terminal panels are centred in the step body, as in the mockup. */
export const TerminalPanel = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${rem(spacing.base)};
  padding: ${rem(spacing.base)} 0;
  text-align: center;
`;

export const TerminalActions = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${rem(spacing.base / 2)};
`;

/**
 * The terminal phases' footer buttons.
 *
 * Wizard's Footer is `flex-direction: row-reverse; justify-content: space-between`
 * and its own `defaultActions` wraps the step buttons in an ActionButtonsContainer
 * for exactly that reason. Returning bare Buttons from `renderActions` instead
 * makes each one a direct flex child, so two of them land at *opposite ends* of
 * the footer, reversed — which is what put the error panel's CLOSE on the far left
 * and BACK on the far right.
 *
 * One wrapper is one flex item, so `space-between` puts it at main-start, which
 * `row-reverse` makes the right edge. Inside, a normal row renders DOM order
 * left to right. 10px matches ActionButtonsContainer's own column-gap.
 *
 * Not imported from the library's Wizard/styles: reaching into a component's
 * internal styled-components would break on any KeyDS bump.
 */
export const FooterActions = styled.div`
  display: flex;
  gap: ${rem(spacing.base / 2)};
`;

/** The creating panel is a single left-aligned row, unlike the other two. */
export const CreatingRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${rem(spacing.base / 2)};
`;

const WARNING_BG = toColorString(color.status.warning.light);
export const EXISTING_USER_WARNING_COLOR = toColorString(
  color.status.warning.medium,
);
const WARNING_BORDER = EXISTING_USER_WARNING_COLOR;

export const ExistingUserCardContainer = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${rem(10)};
  flex: 1 0 0;
  padding: ${rem(11)} ${rem(15)};
  border-left: ${rem(2)} solid ${WARNING_BORDER};
  border-radius: ${rem(2)} 0 0 ${rem(2)};
  background-color: ${WARNING_BG};
`;

export const ExistingUserIconBox = styled.div`
  display: flex;
  flex-shrink: 0;
  width: ${rem(16)};
  height: ${rem(16)};
  justify-content: center;
  align-items: center;
`;

export const ExistingUserContent = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  flex: 1 0 0;
  gap: ${rem(spacing.base / 2)};
`;

export const ExistingUserCardHeader = styled.div`
  display: flex;
  align-items: flex-start;
  align-self: stretch;
`;

export const ExistingUserTextColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  flex: 1 0 0;
`;

export const ExistingUserIdentity = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  align-self: stretch;
  gap: ${rem(8)};
  padding: ${rem(10)};
  background-color: ${toColorString(color.opacity.fullMoon50)};
`;

export const ExistingUserIdentityRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${rem(spacing.base)};
`;

export const AcknowledgmentIdentity = styled.div`
  display: flex;
  align-items: center;
  gap: ${rem(spacing.base)};
  padding: ${rem(spacing.base)};
  border-radius: ${rem(spacing.base / 5)};
  background-color: ${rgbaToHex(
    color.opacity.fullMoon50,
    color.base.snowWhite,
  )};
`;

export const ExistingUserIdentityText = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  flex: 1 0 0;
`;
